import type { ClaimActionPlanInsightMetadata } from '~/components/AiChat/SideBarDialog/Insights/ClaimActionPlan/types';
import type {
  AiChatSchemaAiChatMetadata,
  AiChatSchemaCliveReferenceData,
  AiChatSchemaFirebaseWebAppConfigResponse,
  AiChatSchemaFirestoreConnectionConfig,
  AiChatSchemaFirestoreQueryConfig,
  AiChatSchemaIssueTokenResponse,
  AsyncChatSubmitResponse as AsyncChatSubmitResponseGenerated,
  ClaimActionsListResponseClaimActionActor,
  ClaimActionsListResponseClaimActionsDemoReserve,
  ClaimActionsListResponseClaimActionStatusChange,
  UpdateExposureLineItemsRequestCoverageDecisionOption,
  WebpagePreviewResponseWebpagePreviewData,
} from '~/server_shared/openapi/data-contracts';

export type AiSelectedTab = 'actions_tab' | 'insights_tab' | 'chat_tab' | 'history_tab';

// Use the generated backend type for reference data
export type AiChatReference = AiChatSchemaCliveReferenceData;

export interface AiChatReferences {
  [key: string]: AiChatReference;
}

// Re-export backend types with minimal refinements to fix type incompatibilities
// Most fields work as-is, only fixing what breaks in practice

export interface FirebaseWebAppConfig extends Omit<AiChatSchemaFirebaseWebAppConfigResponse, 'database_url'> {
  database_url?: string; // Backend: string | null (Firebase SDK doesn't accept null)
}

export type FirestoreConnectionConfig = AiChatSchemaFirestoreConnectionConfig; // Works as-is

export interface FirestoreQueryConfig extends Omit<AiChatSchemaFirestoreQueryConfig, 'where_clauses'> {
  where_clauses: Array<{ field: string; operator: string; value: unknown }>; // Backend: object[] (too generic)
  // order_by works as-is: Record<string, string>[] | null is fine
}

export interface IssueTokenResponse
  extends Omit<AiChatSchemaIssueTokenResponse, 'firebase_web_app_config' | 'query_config'> {
  firebase_web_app_config: FirebaseWebAppConfig; // Uses refined type
  query_config: FirestoreQueryConfig; // Uses refined type
  // firestore_config works as-is
}

export type AiChatMetadata = AiChatSchemaAiChatMetadata;

export interface AiChatApiResponse {
  id: string;
  internal_id: number | null;
  greeting: string;
  chat_title?: string;
  exchanges: AiChatExchangeApiResponse[];
  references: AiChatReferences;
  issue_token_response?: IssueTokenResponse;
  metadata?: AiChatMetadata;
}

export interface AiChatExchangeApiResponse {
  id: number;
  chat_id: number | null;
  user_input: string;
  response: string | null;
  response_time: number | null;
  user_feedback_score?: number;
  user_feedback_text?: string;
  documents_used: string[];
  context_document_ids: number[];
  created_at: Date;
}

export type UpdateExchangeUserFeedbackCallback = (
  exchangeId: number,
  feedbackScore: number,
  feedbackText: string
) => Promise<boolean>;

export interface AiChatReferenceApiResponse {
  id: string;
  name: string;
  type: 'note' | 'document' | 'communication';
  date?: Date;
}

export interface AiChatExchangesAndReferencesApiResponse {
  exchanges: AiChatExchangeApiResponse[];
  references: AiChatReferences;
}

// Generated 202 handle, refining real_time_token to the FE IssueTokenResponse (the generated
// real_time_token carries the same database_url null incompatibility fixed above). status is
// claims_llm's ExchangeStatus: processing | completed | failed.
export interface AsyncChatSubmitResponse extends Omit<AsyncChatSubmitResponseGenerated, 'real_time_token'> {
  real_time_token?: IssueTokenResponse;
}

export interface AiChatStatusApiResponse {
  status?: 'SUCCESS' | 'FAILED' | 'PENDING';
  type?: 'communication' | 'document' | 'note';
  updated_at?: Date;
}

export interface InsightDocument {
  document_name: string;
  document_id?: string;
  document_type?: string;
  document_url?: string;
}

// A select/multiselect option's value → label mapping, as the backend display spec serializes it.
export interface DisplaySpecFieldOption {
  id: string;
  desc: string;
}

// One structured-output field's display metadata, looked up by `key` in `structured_output`.
// `type` is a ConfiguredFieldType value (e.g. 'boolean', 'select', 'monetary'), or `model` /
// `model_list` for an object / list-of-objects value whose keys `fields` describes; kept as a
// string so the renderer can dispatch on it without coupling the FE to the backend enum.
export interface DisplaySpecField {
  key: string;
  label: string;
  type: string;
  options?: DisplaySpecFieldOption[] | null;
  fields?: DisplaySpecField[] | null;
}

// CMS-owned display spec for an insight's structured output: which render component to use and the
// per-field display metadata, in display order. Derived server-side from the producing expert's
// override when one resolves, else from the `structured_output_schema` the AI service published with
// the insight (the only source of a nested `sections` spec).
export interface DisplaySpec {
  render_component: string;
  fields: DisplaySpecField[];
}

export interface InsightExpertPayload {
  expert_key?: string;
  expert_name?: string;
  confidence_score?: number;
  confidence_level?: 'low' | 'medium' | 'high' | string;
  confidence_explanation?: string;
  icon?: string;
  // { field_id: value } map produced by the expert (arrives nested in the insight payload); values
  // may be objects or lists of objects for composite fields.
  structured_output?: Record<string, unknown>;
  // CMS-provided render instructions for `structured_output` (enriched read-time, alongside `icon`).
  display_spec?: DisplaySpec;
}

export interface Insight {
  id: string;
  insight_key: string;
  claim_number: string;
  insight_type:
    | 'data_extraction'
    | 'data_point'
    | 'warning'
    | 'general'
    | 'financial'
    | 'complexity'
    | 'expert_analysis';
  title: string;
  sub_title: string;
  insight_text: string;
  insight_metadata?: Record<string, unknown> | ClaimActionPlanInsightMetadata;
  payload?: InsightExpertPayload | Record<string, unknown>;
  // Nullable: an insight citing nothing serializes `related_documents` as null, matching the
  // server schema's `Optional[list[AiInsightDocument]]`.
  related_documents: InsightDocument[] | null;
  is_dismissed?: boolean;
  // ISO datetime, as it comes off the wire. When this version was generated: insights are
  // append-only, so this — not `updated_at` — is the insight's real time, since `updated_at` only
  // moves when the row is dismissed.
  created_at?: string;
  updated_at?: Date;
  // Older versions of this insight_key, excluding this one. Drives "View history".
  // Undefined means the service did not report it, which is not the same as zero.
  history_count?: number;
}

export interface AiDoneActionNote {
  action_type?: string;
  note_text: string;
}

export const AI_ACTION_TYPES = {
  INVOICE_ANALYSIS_RECOMMENDATION: 'invoice_analysis_recommendation',
  ASSESS_DAMAGE: 'assess_damage',
  COMMUNICATE: 'communicate',
  ADJUSTER_ASSIGNMENT: 'adjuster_assignment',
  OPEN_EXPOSURE: 'open_exposure',
  SET_RESERVE: 'set_reserve', // used for demo purposes
  // Unified task type emitted by the dynamic-action agent; the concrete tool lives in `tool_key`,
  // so a single generic descriptor renders all of them.
  DYNAMIC_CLAIM_ACTION: 'dynamic_claim_action',
};

export type AiActionType = (typeof AI_ACTION_TYPES)[keyof typeof AI_ACTION_TYPES];

export interface ActionTaskData {
  task_id?: string;
  task_text: string;
  explanation: string;
  additional_actions?: string;
  done_action?: AiDoneActionNote;
  related_exposure_id?: string;
  task_type?: string;
}

export interface InvoiceAnalysisRecommendationTaskData extends ActionTaskData {
  task_type: ClaimTaskType;
  analysis_results_id: string;
  claim_document_source_id: string;
  invoice_date?: string;
  invoice_number?: string;
  document_type?: string;
  vendor_name?: string;
  tax_rate?: number;
  tax_amount?: number;
  total_amount?: number;
  subtotal?: number;
  file_path?: string;
  document_claim_internal_id?: number;
  line_items: InvoiceAnalysisRecommendationLineItem[];
  coverages: CoverageType[];
}

interface InvoiceAnalysisRecommendationLineItem {
  id: string;
  quantity?: number;
  unit_price?: number;
  description?: string;
  total_price?: number;
  covered_amount?: number;
  coverage_decision?: UpdateExposureLineItemsRequestCoverageDecisionOption;
  coverage_key?: string;
  explanation?: string;
}

interface CoverageType {
  coverage_key: string;
  coverage_display_name: string;
}

export interface AssessDamageTaskData extends ActionTaskData {
  related_exposure_id?: string;
}

export interface CommunicateTaskData extends ActionTaskData {
  subject?: string;
  content?: string;
  recipient_name?: string;
  recipient_email?: string;
  recipient_id?: string;
}

// The unified task the dynamic-action agent emits. The concrete tool is carried in `tool_key`
// and re-resolved server-side at execute time; `envelope` is the exact {payload, metadata} body
// POSTed to CMS. `envelope.payload` holds the fields the tool writes to the claim, `exposure_id`
// included where the tool is exposure-scoped.
export interface DynamicClaimActionTaskData extends ActionTaskData {
  task_type: 'dynamic_claim_action';
  tool_key: string;
  envelope: {
    payload: Record<string, unknown>;
    metadata: Record<string, unknown>;
  };
  /**
   * Why CMS held the action for a person, as CMS answered the POST: `key` names the cause and `text`
   * is the sentence to show. Null (or absent) on actions held for no reason or saved before the AI
   * service kept it. The key is a plain string because the AI service stores whatever CMS sent, so it
   * may be one this build does not know.
   */
  requires_human_approval_reason?: { key: string; text: string } | null;
}

// A guard rather than a `task_type` comparison: other members of the union type `task_type` as a
// plain string, so comparing it does not narrow.
export const isDynamicClaimActionTaskData = (taskData?: TaskData): taskData is DynamicClaimActionTaskData =>
  taskData?.task_type === 'dynamic_claim_action';

type ClaimTaskType = 'invoice_analysis_recommendation';

type TaskData =
  | InvoiceAnalysisRecommendationTaskData
  | CommunicateTaskData
  | AssessDamageTaskData
  | DynamicClaimActionTaskData;

/**
 * Who executed or dismissed an action. `type` is set server-side by the entry path the
 * transition came in on — a client payload can never supply it, so `agent` really is Clive.
 *
 * `user_id` is the one stable handle on the person: `display_name` is a snapshot taken when the
 * action was settled and does not follow a rename. Both are absent where the AI service kept no
 * identity, and `user_id` is also absent on transitions recorded before CMS started sending it.
 */
export type ActionActor = ClaimActionsListResponseClaimActionActor;

/**
 * The terminal transition of an action, as the AI service recorded it. Null until the action is
 * executed or dismissed — a pending action has none. `reason` carries the handler's rejection note
 * or Clive's account of the withdrawal. `at` is ISO-8601, and is not `updated_at`, which any later
 * write moves.
 */
export type ActionStatusChange = ClaimActionsListResponseClaimActionStatusChange;

export interface AiAction {
  is_executed: boolean;
  is_ignored: boolean;
  action_id: string;
  related_exposure_id?: string;
  created_at: Date;
  updated_at: Date;
  task_data: TaskData;
  status_change?: ActionStatusChange | null;
  history_count?: number;
  revision?: number;
}

export interface Exposure {
  coverage_type_desc: string;
  handling_adjuster: string;
  label_text: string;
}

// The backend builds this (`get_claim_actions_additional_data`) and declares it
// (`ClaimActionsDemoReserve`); the hand-written copy that used to live here typed the ids and
// amounts as strings, which they have never been on the wire.
export type ReserveActionData = ClaimActionsListResponseClaimActionsDemoReserve;

// Re-export generated types from backend with better naming
export type WebpagePreviewData = WebpagePreviewResponseWebpagePreviewData;

export interface WebpagePreviews {
  [url: string]: WebpagePreviewData;
}
